import { InfoOutlined, UploadFile } from "@mui/icons-material";
import {
    Alert,
    AlertTitle,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Collapse,
    IconButton,
    LinearProgress,
    Typography,
} from "@mui/material";
import { useState } from "react";
import * as XLSX from "xlsx";
import { DataImportService } from "../../../Services/DataImportService";

// Helper function to read Excel file and create collections
const readExcelFileAndCreateCollections = async (
    bin: string | ArrayBuffer | null | undefined
): Promise<any[]> => {
    console.log("in readExcelFileAndCreateCollections");
    const wb = XLSX.read(bin, { type: "binary" });

    const sheets = wb.Sheets;

    let collectionFailures: any[] = [],
        collectionSuccesses: any[] = [];

    // Loop through each sheet in the workbook
    for (const sheet of Object.values(sheets)) {
        // Convert sheet to JSON and format dates
        const json = XLSX.utils.sheet_to_json(sheet, {
            raw: false, // Ensures dates are parsed as strings, not raw numbers
            dateNF: "yyyy-mm-dd", // Desired format for dates
        });

        const parsedJson = json.map((row: any) => {
            return row;
        });

        // Call service to create reading collections
        const [data, err] = await DataImportService.createAddtionCharges(
            parsedJson
        );

        // Populate successes and failures based on service response
        if (data) {
            collectionSuccesses = [
                ...collectionSuccesses,
                ...(data.successes || []),
            ];
            collectionFailures = [
                ...collectionFailures,
                ...(data.failures || []),
            ];
        } else if (err) {
            collectionSuccesses = [
                ...collectionSuccesses,
                ...(err.successes || []),
            ];
            collectionFailures = [
                ...collectionFailures,
                ...(err.failures || []),
            ];
        }
    }

    return [collectionSuccesses, collectionFailures];
};

export const AdditionalChargesImport = () => {
    // State for loading status: 'initial', 'loading', 'finished'
    const [loading, setLoading] = useState<"loading" | "initial" | "finished">(
        "initial"
    );
    // State for the name of the selected file
    const [fname, setFname] = useState("");

    // RENAMED: paymentMethod -> paymentMethods (as it's an array)

    // State for successful and failed collection imports
    const [successes, setSucesses] = useState<any[]>([]);
    const [failures, setFailures] = useState<any[]>([]);
    // State to control visibility of response details (successes/failures list)
    const [showResponseDetails, setShowResponseDetails] = useState(false);
    // State to hold the selected file object
    const [file, setFile] = useState<File | null>(null);
    // NEW STATE: State to control the visibility of the "no file selected" alert
    const [showNoFileAlert, setShowNoFileAlert] = useState(false);

    // Handler for file upload process
    const handleFileUpload = async () => {
        // NEW LOGIC: Check if a file is selected
        if (!file) {
            setShowNoFileAlert(true); // Show the alert if no file
            return; // Stop the function execution
        }

        setShowNoFileAlert(false); // Hide the alert if a file is selected and we proceed

        setFname(file.name); // Set the file name for display
        const reader = new FileReader(); // Create a new FileReader instance

        setLoading("loading"); // Set loading state to indicate processing

        // Define what happens when the file is done loading
        reader.onload = async (evt) => {
            const binary = evt.target?.result; // Get the file content as binary string

            // Read the Excel data and create collections
            const [_successes, _failures] =
                await readExcelFileAndCreateCollections(binary);

            console.log("_successes", _successes, "_failures", _failures);

            setFailures(_failures); // Update failures state
            setSucesses(_successes); // Update successes state

            setLoading("finished"); // Set loading state to finished
        };

        reader.readAsBinaryString(file); // Read the file as a binary string
    };

    return (
        <Card>
            <CardContent>
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    mb={2} // Margin bottom for spacing
                >
                    <Typography variant="h5" fontWeight="bold">
                        Import Additional Charges
                    </Typography>

                    <Box display={"flex"} gap={1}>
                        {/* Upload File Button */}
                        <Button
                            variant="contained"
                            color="secondary"
                            disabled={loading === "loading"}
                            startIcon={
                                loading === "loading" ? (
                                    <CircularProgress size={20} />
                                ) : (
                                    <UploadFile />
                                )
                            }
                            onClick={handleFileUpload} // Call the handler directly
                        >
                            Upload File
                        </Button>
                        {/* Download Template Button */}
                    </Box>
                </Box>

                <div>
                    {/* File Input */}
                    <div>
                        <input
                            onChange={(e) => {
                                setFile(
                                    e.target.files?.length
                                        ? e.target.files[0]
                                        : null
                                );
                                // NEW LOGIC: Hide the "no file" alert when a file is chosen/deselected
                                setShowNoFileAlert(false);
                            }}
                            type="file"
                            accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                            style={{ marginBottom: "10px" }} // Small style adjustment for spacing
                        />
                        {fname && ( // Display selected file name if available
                            <Typography
                                variant="body2"
                                color="textSecondary"
                                sx={{ mt: 1 }}
                            >
                                Selected file: <strong>{fname}</strong>
                            </Typography>
                        )}
                    </div>

                    {/* NEW: "No File Selected" Alert - IMPROVED MESSAGE */}
                    {showNoFileAlert && (
                        <Alert severity="warning" sx={{ mt: 2 }}>
                            <AlertTitle>No File Selected</AlertTitle>
                            Please choose an Excel file (.xlsx) using the file
                            input above before clicking 'Upload File'.
                        </Alert>
                    )}

                    {/* Instructions and Hints - REFORMATTED AND MADE MEANINGFUL */}

                    {/* Linear Progress Bar during loading */}
                    {loading === "loading" && <LinearProgress sx={{ mt: 2 }} />}

                    {/* Section to display results (successes/failures) */}
                    <section
                        hidden={loading !== "finished"}
                        style={{ marginTop: "20px" }}
                    >
                        <Alert
                            severity="info"
                            action={
                                <IconButton
                                    onClick={() => {
                                        setShowResponseDetails(
                                            !showResponseDetails
                                        );
                                    }}
                                >
                                    <InfoOutlined fontSize="small" />
                                </IconButton>
                            }
                        >
                            <AlertTitle>
                                {`${successes?.length} successes out of ${
                                    (successes?.length ?? 0) +
                                    (failures?.length ?? 0)
                                } `}
                            </AlertTitle>

                            {/* Collapse to show/hide detailed results */}
                            <Collapse in={showResponseDetails}>
                                {successes?.length > 0 && (
                                    <Typography
                                        variant="subtitle2"
                                        sx={{ mt: 1, mb: 0.5 }}
                                    >
                                        Successful Imports:
                                    </Typography>
                                )}
                                {successes?.map(
                                    (
                                        suc: {
                                            message: string;
                                            meterNo: number | string;
                                            amount: string | number;
                                        },
                                        index: number
                                    ) => (
                                        <Box
                                            key={`success-${index}`}
                                            sx={{ fontSize: "0.85rem" }}
                                        >
                                            {`Meter No - (${suc.meterNo}) - ${suc.message} - ${suc.amount}`}
                                        </Box>
                                    )
                                )}

                                {failures.length > 0 && (
                                    <Typography
                                        variant="subtitle2"
                                        sx={{
                                            mt: 1,
                                            mb: 0.5,
                                            color: "error.main",
                                        }}
                                    >
                                        Failed Imports:
                                    </Typography>
                                )}
                                {failures.map(
                                    (
                                        err: {
                                            message: string;
                                            meterNo: number | string;
                                            amount: string | number;
                                        },
                                        index: number
                                    ) => (
                                        <Box
                                            key={`failure-${index}`}
                                            sx={{
                                                fontSize: "0.85rem",
                                                color: "error.main",
                                            }}
                                        >
                                            {`Meter No - (${err.meterNo}) - ${err.message} - ${err.amount}`}
                                        </Box>
                                    )
                                )}
                            </Collapse>
                        </Alert>
                    </section>
                </div>
            </CardContent>
        </Card>
    );
};

export default AdditionalChargesImport;
